import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react';
import type { FormFileEntry, FormStateValues } from './types';

/**
 * Result of uploading one picker's pending entries.
 *
 *   - `success` is `false` if at least one upload failed; the picker
 *     surfaces its own per-entry error UI, so the form only needs to know
 *     whether to abort before validating + submitting.
 *   - `value` is the form-shape value for that field _after_ the upload
 *     attempt. Successful committed entries are present; errored entries
 *     are omitted so the validator surfaces them as missing on next submit.
 */
export interface FieldUploadResult {
  success: boolean;
  value: FormFileEntry[];
}

export type UploadPendingFn = () => Promise<FieldUploadResult>;

interface RegistryValue {
  /**
   * Register a picker's `uploadPending` callback. Returns the unsubscribe
   * function — call it on unmount so stale closures don't run.
   */
  register: (fieldTitle: string, fn: UploadPendingFn) => () => void;
  /**
   * Invoked by the form on submit. Calls every registered picker's
   * `uploadPending` in parallel and returns an overlay of new field
   * values plus an aggregate success flag.
   */
  uploadAllPending: () => Promise<{
    success: boolean;
    fieldValues: FormStateValues;
  }>;
}

const FormPendingUploadsContext = createContext<RegistryValue | null>(null);

export const FormPendingUploadsProvider = ({ children }: { children: ReactNode }) => {
  const registrations = useRef<Map<string, UploadPendingFn>>(new Map());

  const register = useCallback((fieldTitle: string, fn: UploadPendingFn) => {
    registrations.current.set(fieldTitle, fn);
    return () => {
      // Only delete if we're still the registered fn — guards against
      // a re-register/unregister race when the picker re-mounts.
      if (registrations.current.get(fieldTitle) === fn) {
        registrations.current.delete(fieldTitle);
      }
    };
  }, []);

  const uploadAllPending = useCallback(async () => {
    const entries = Array.from(registrations.current.entries());
    if (entries.length === 0) {
      return { success: true, fieldValues: {} as FormStateValues };
    }
    const results = await Promise.all(
      entries.map(async ([title, fn]) => {
        const result = await fn();
        return { title, ...result };
      }),
    );
    let success = true;
    const fieldValues: FormStateValues = {};
    for (const r of results) {
      if (!r.success) success = false;
      fieldValues[r.title] = r.value;
    }
    return { success, fieldValues };
  }, []);

  const value: RegistryValue = { register, uploadAllPending };
  return (
    <FormPendingUploadsContext.Provider value={value}>
      {children}
    </FormPendingUploadsContext.Provider>
  );
};

/**
 * Returns the registry, or `null` when no provider wraps the tree. Pickers
 * tolerate the null case (no registration → upload-immediately behaviour
 * stays available for any caller that hasn't opted into deferred uploads).
 */
export const useFormPendingUploads = (): RegistryValue | null => {
  return useContext(FormPendingUploadsContext);
};
